import { useCallback, useEffect, useState } from "react";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "../../lib/api";
import { Thumb } from "./ProductTile";
import StockPicker from "./StockPicker";
import PaymentMethods from "./PaymentMethods";
import { rp } from "./util";

const err = (e, m) => toast.error(formatApiError(e.response?.data?.detail) || m);
export const srcOf = (img) => (img?.type === "catalog" ? fileUrl(img.src) : null);
const num = "h-8 w-24 rounded border border-mist bg-white px-2 text-sm text-right outline-none focus:border-sage-dark";

export default function DraftEditor({ eventId, onBack, onStarted }) {
    const [ev, setEv] = useState(null);
    const [items, setItems] = useState([]);
    const [picking, setPicking] = useState(false);
    const load = useCallback(() => api.get(`/pos/events/${eventId}`).then(({ data }) => { setEv(data.event); setItems(data.items); }).catch((e) => err(e, "Could not load event")), [eventId]);
    useEffect(() => { load(); }, [load]);

    const patch = async (it, body) => {
        try {
            const { data } = await api.patch(`/pos/events/${eventId}/items/${it.item_id}`, body);
            setItems((xs) => xs.map((x) => (x.item_id === it.item_id ? data : x)));
        } catch (e) { err(e, "Could not update"); load(); }
    };
    const remove = async (it) => {
        await api.delete(`/pos/events/${eventId}/items/${it.item_id}`).catch((e) => err(e, "Could not remove"));
        setItems((xs) => xs.filter((x) => x.item_id !== it.item_id));
    };
    const start = async () => {
        if (!window.confirm(`Start "${ev.name}"?\n\nEvent stock (${items.length} SKUs, ${items.reduce((s, i) => s + i.opening_qty, 0)} pcs) will be LOCKED. No Jubelio sync or stock additions during the event.`)) return;
        try {
            await api.post(`/pos/events/${eventId}/start`);
            toast.success("Event started · stock locked");
            onStarted();
        } catch (e) { err(e, "Could not start event"); }
    };
    if (!ev) return <p className="text-sm text-neutral-400">Loading…</p>;
    if (picking) return <StockPicker eventId={eventId} existing={items} onBack={() => setPicking(false)} onImported={(xs) => { setItems(xs); setPicking(false); }} />;
    const zero = items.filter((i) => i.price <= 0).length;
    return (
        <div className="space-y-4" data-testid="pos-draft-editor">
            <button onClick={onBack} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-neutral-500 hover:text-ink" data-testid="pos-draft-back"><ArrowLeft size={14} /> Events</button>
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="text-[10px] uppercase tracking-widest text-neutral-500">Draft event · {ev.location_name} / {ev.location_code}</p>
                    <p className="font-serif text-2xl text-ink">{ev.name}</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setPicking(true)} className="h-10 rounded border border-sage-dark px-4 text-xs uppercase tracking-widest text-sage-dark" data-testid="pos-load-warehouse">Import From Warehouse</button>
                    <button onClick={start} disabled={!items.length || zero > 0} className="h-10 rounded bg-sage-dark px-4 text-xs uppercase tracking-widest text-white disabled:opacity-40" data-testid="pos-start-event">Start Event</button>
                </div>
            </div>
            <PaymentMethods ev={ev} />
            {zero > 0 && <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="pos-zero-price-warning">{zero} item(s) have no selling price. Set a price before starting.</p>}
            <div className="overflow-x-auto rounded-md border border-mist bg-white">
                <table className="w-full min-w-[860px] text-sm" data-testid="pos-event-stock-table">
                    <thead className="bg-ivory text-[10px] uppercase tracking-widest text-neutral-500">
                        <tr>{["", "Product", "SKU / Barcode", "Photo", "Warehouse", "Event Qty", "Price", ""].map((h, i) => <th key={i} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-mist">
                        {items.map((it) => (
                            <tr key={it.item_id} data-testid={`pos-event-item-${it.item_id}`}>
                                <td className="px-3 py-2"><Thumb src={srcOf(it.image)} className="h-10 w-10 rounded" /></td>
                                <td className="px-3 py-2"><p className="text-ink">{it.name}</p><p className="text-[11px] text-neutral-500">{it.variant}</p></td>
                                <td className="px-3 py-2 text-xs"><p>{it.sku}</p><p className="text-neutral-400">{it.barcode || "no barcode"}</p></td>
                                <td className="px-3 py-2 text-[11px] uppercase tracking-widest text-neutral-500">{it.image?.type}</td>
                                <td className="px-3 py-2">{it.warehouse_available}</td>
                                <td className="px-3 py-2"><input type="number" min={1} max={it.warehouse_available} defaultValue={it.opening_qty} className={num} data-testid={`pos-event-qty-${it.item_id}`}
                                    onBlur={(e) => Number(e.target.value) !== it.opening_qty && patch(it, { opening_qty: Number(e.target.value) })} /></td>
                                <td className="px-3 py-2"><input type="number" min={0} defaultValue={it.price} className={`${num} w-32 ${it.price <= 0 ? "border-amber-500" : ""}`} data-testid={`pos-event-price-${it.item_id}`}
                                    onBlur={(e) => Number(e.target.value) !== it.price && patch(it, { price: Number(e.target.value) })} />
                                    <p className="text-[10px] text-neutral-400 mt-0.5">{rp(it.price)}</p></td>
                                <td className="px-3 py-2"><button onClick={() => remove(it)} className="text-xs text-red-700" data-testid={`pos-event-remove-${it.item_id}`}>Remove</button></td>
                            </tr>
                        ))}
                        {!items.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-neutral-400">No event stock yet. Import from the warehouse.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
